import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { UsernameSetup } from "@/components/UsernameSetup";
import { LandingPage } from "@/components/LandingPage";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
interface CompanyData {
  name: string;
  personName: string;
  phone: string;
  email: string;
}

const Index = () => {
  const { user, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRegistered, setIsRegistered] = useState(false);
  const [hasUsername, setHasUsername] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [userType, setUserType] = useState<string | null>(null);
  const { currentOrganization } = useOrganization();

  // Remove automatic redirect to auth - show landing page instead

  // Check if user has completed profile setup
  useEffect(() => {
    const checkUserProfile = async () => {
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (profile) {
          setUserType(profile.user_type);
          
          // Get user's organization data using bypass function to avoid RLS issues
          const { data: orgIds, error: orgIdsError } = await supabase
            .rpc('get_user_org_ids_bypass_rls', { user_uuid: user.id });

          let orgName = null;
          let username = null;

          if (!orgIdsError && orgIds && orgIds.length > 0) {
            // Get organization details for the first organization
            const { data: org, error: orgDetailsError } = await supabase
              .from('organizations')
              .select('username, name')
              .eq('id', orgIds[0])
              .single();

            if (!orgDetailsError && org) {
              orgName = org.name;
              username = org.username;
            }
          }

          console.log('Debug: User profile found - userType:', profile.user_type);
          console.log('Debug: Organization data - orgName:', orgName, 'username:', username);
          console.log('Debug: Organization IDs:', orgIds);

          if (orgName) {
            // User has an organization - they're fully set up
            console.log('Debug: User has organization, setting as fully registered');
            setCompanyData({
              name: orgName,
              personName: profile.full_name || '',
              phone: profile.phone || '',
              email: profile.email || ''
            });
            setIsRegistered(true);
            setHasUsername(!!username);
          } else {
            // User has profile but no organization yet - they need username setup
            console.log('Debug: User has no organization, needs username setup');
            // Use company data from signup metadata
            setCompanyData({
              name: user.user_metadata?.company_name || profile.full_name || 'My Company',
              personName: profile.full_name || '',
              phone: profile.phone || '',
              email: profile.email || ''
            });
            setIsRegistered(true); // Skip company registration since we have the data from signup
            setHasUsername(false); // Go directly to username setup
          }
        } else {
          console.log('Debug: No user profile found for user:', user.id);
        }
      }
      setProfileLoading(false);
    };

    if (!loading) {
      if (user) {
        checkUserProfile();
      } else {
        // No user, so no need to load profile - set profileLoading to false immediately
        setProfileLoading(false);
      }
    }
  }, [user, loading]);

  // Redirect logged-in users to appropriate dashboard when setup complete
  useEffect(() => {
    if (!loading && !profileLoading && user && isRegistered && hasUsername) {
      const orgIsCarrier = currentOrganization ? currentOrganization.organization_type !== 'customer' : null as unknown as boolean | null;
      const isCarrier = orgIsCarrier !== null ? (orgIsCarrier as boolean) : ['carrier_company', 'broker', 'driver'].includes(userType || '');
      navigate(isCarrier ? '/carrier-dashboard' : '/customer-dashboard', { replace: true });
    }
  }, [loading, profileLoading, user, isRegistered, hasUsername, userType, navigate]);

  // Show loading while auth is initializing or profile is loading
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If auth has finished loading and no user, show landing page
  if (!user) {
    return <LandingPage />;
  }

  // Skip company registration since user provided data during signup
  // Go directly to username setup or dashboard based on setup status

  // Username setup flow  
  if (isRegistered && !hasUsername) {
    const returnUrl = searchParams.get('returnUrl');
    
    return (
      <div>
        <div className="absolute top-4 right-4">
          <Button variant="outline" onClick={signOut}>
            Sign Out
          </Button>
        </div>
        <div className="min-h-screen flex items-center justify-center">
          <UsernameSetup 
            onComplete={() => setHasUsername(true)} 
            returnUrl={returnUrl ? decodeURIComponent(returnUrl) : undefined}
          />
        </div>
      </div>
    );
  }

  // User is logged in and setup complete; a redirect is in progress
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" aria-label="Redirecting to your dashboard"></div>
    </div>
  );
};

export default Index;
